import { useState } from "react";
import { BLOCK_CATALOG, type Block, type BlockType } from "@alchimie/shared";
import { Field, Input, Textarea, Button, COLORS } from "../ui.js";
import { TiptapEditor } from "./TiptapEditor.js";
import { AssetSelector } from "./AssetSelector.js";
import { ColorInput } from "./StyleEditor.js";
import { createBlock } from "./defaults.js";

interface Props {
  block: Block;
  onChange: (next: Block) => void;
  onPersist?: () => void;
  /** Callback pour drill-in dans un bloc imbriqué d'une colonne. */
  onEnterNested?: (colIdx: number, nestedId: string) => void;
}

// Types de blocs interdits à l'intérieur d'une colonne (évite la récursion infinie
// et les structures lourdes sans valeur ajoutée dans une colonne).
const NESTABLE_TYPES: BlockType[] = [
  "RICH_TEXT", "VIDEO", "AUDIO", "PDF", "IMAGE",
  "BENEFITS", "TESTIMONIALS", "FAQ", "CTA",
  "DIVIDER", "SPACER", "CUSTOM_HTML",
];

/**
 * Éditeur de propriétés d'un bloc — affiché dans le panneau de droite du Builder.
 * Délègue à un sous-composant selon le type.
 */
export function PropertyEditor({ block, onChange, onPersist, onEnterNested }: Props) {
  switch (block.type) {
    case "HERO":
      return (
        <Group label="Bloc Hero">
          <Field label="Titre">
            <Input
              value={block.props.title}
              onChange={(e) => onChange({ ...block, props: { ...block.props, title: e.target.value } })}
            />
          </Field>
          <Field label="Sous-titre">
            <Textarea
              value={block.props.subtitle ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, subtitle: e.target.value || undefined } })}
            />
          </Field>
          <Field label="Libellé du bouton (optionnel)">
            <Input
              value={block.props.ctaLabel ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, ctaLabel: e.target.value || undefined } })}
              placeholder="ex : Découvrir l'offre"
            />
          </Field>
        </Group>
      );

    case "RICH_TEXT":
      return (
        <Group label="Texte riche">
          <TiptapEditor
            doc={block.props.doc}
            onChange={(doc) => onChange({ ...block, props: { ...block.props, doc } })}
            placeholder="Écrivez ici votre paragraphe…"
          />
        </Group>
      );

    case "VIDEO":
      return (
        <Group label="Vidéo">
          <Field label="Source">
            <AssetSelector
              kind="VIDEO"
              selectedId={block.props.assetId}
              onSelect={(a) => onChange({ ...block, props: { ...block.props, assetId: a.id } })}
            />
          </Field>
          <Field label="Légende">
            <Input
              value={block.props.caption ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, caption: e.target.value || undefined } })}
            />
          </Field>
        </Group>
      );

    case "AUDIO":
      return (
        <Group label="Audio">
          <Field label="Source">
            <AssetSelector
              kind="AUDIO"
              selectedId={block.props.assetId}
              onSelect={(a) => onChange({ ...block, props: { ...block.props, assetId: a.id } })}
            />
          </Field>
          <Field label="Titre (optionnel)">
            <Input
              value={block.props.title ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, title: e.target.value || undefined } })}
            />
          </Field>
        </Group>
      );

    case "PDF":
      return (
        <Group label="PDF">
          <Field label="Source">
            <AssetSelector
              kind="PDF"
              selectedId={block.props.assetId}
              onSelect={(a) => onChange({ ...block, props: { ...block.props, assetId: a.id } })}
            />
          </Field>
          <Field label="Titre (optionnel)">
            <Input
              value={block.props.title ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, title: e.target.value || undefined } })}
            />
          </Field>
          <Toggle
            label="Téléchargeable"
            checked={block.props.downloadable}
            onChange={(v) => onChange({ ...block, props: { ...block.props, downloadable: v } })}
          />
        </Group>
      );

    case "IMAGE":
      return (
        <Group label="Image">
          <Field label="Source">
            <AssetSelector
              kind="IMAGE"
              selectedId={block.props.assetId}
              onSelect={(a) => onChange({ ...block, props: { ...block.props, assetId: a.id } })}
            />
          </Field>
          <Field label="Texte alternatif (a11y)">
            <Input
              value={block.props.alt ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, alt: e.target.value || undefined } })}
            />
          </Field>
          <Field label="Légende">
            <Input
              value={block.props.caption ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, caption: e.target.value || undefined } })}
            />
          </Field>
        </Group>
      );

    case "BENEFITS":
      return (
        <Group label="Bénéfices">
          <Field label="Titre de la section">
            <Input
              value={block.props.heading ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, heading: e.target.value || undefined } })}
            />
          </Field>
          <Field label="Colonnes">
            <select
              value={block.props.columns}
              onChange={(e) => onChange({ ...block, props: { ...block.props, columns: Number(e.target.value) } })}
              style={selectStyle}
            >
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </Field>
          <ItemList
            label="Items"
            items={block.props.items}
            empty={() => ({ icon: "✨", title: "Nouveau bénéfice", body: "" })}
            onChange={(items) => onChange({ ...block, props: { ...block.props, items } })}
            renderFields={(it, set) => (
              <>
                <Field label="Icône (emoji)">
                  <Input value={it.icon ?? ""} onChange={(e) => set({ ...it, icon: e.target.value || undefined })} />
                </Field>
                <Field label="Titre">
                  <Input value={it.title} onChange={(e) => set({ ...it, title: e.target.value })} />
                </Field>
                <Field label="Description">
                  <Textarea value={it.body ?? ""} onChange={(e) => set({ ...it, body: e.target.value || undefined })} />
                </Field>
              </>
            )}
          />
        </Group>
      );

    case "TESTIMONIALS":
      return (
        <Group label="Témoignages">
          <Field label="Titre de la section">
            <Input
              value={block.props.heading ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, heading: e.target.value || undefined } })}
            />
          </Field>
          <ItemList
            label="Témoignages"
            items={block.props.items}
            empty={() => ({ author: "Nouveau Client", quote: "", role: undefined as string | undefined })}
            onChange={(items) => onChange({ ...block, props: { ...block.props, items } })}
            renderFields={(it, set) => (
              <>
                <Field label="Citation">
                  <Textarea value={it.quote} onChange={(e) => set({ ...it, quote: e.target.value })} />
                </Field>
                <Field label="Auteur">
                  <Input value={it.author} onChange={(e) => set({ ...it, author: e.target.value })} />
                </Field>
                <Field label="Rôle / fonction">
                  <Input value={it.role ?? ""} onChange={(e) => set({ ...it, role: e.target.value || undefined })} />
                </Field>
              </>
            )}
          />
        </Group>
      );

    case "FAQ":
      return (
        <Group label="FAQ">
          <Field label="Titre">
            <Input
              value={block.props.heading ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, heading: e.target.value || undefined } })}
            />
          </Field>
          <ItemList
            label="Questions"
            items={block.props.items}
            empty={() => ({ q: "Nouvelle question", a: "Réponse claire." })}
            onChange={(items) => onChange({ ...block, props: { ...block.props, items } })}
            renderFields={(it, set) => (
              <>
                <Field label="Question">
                  <Input value={it.q} onChange={(e) => set({ ...it, q: e.target.value })} />
                </Field>
                <Field label="Réponse">
                  <Textarea value={it.a} onChange={(e) => set({ ...it, a: e.target.value })} />
                </Field>
              </>
            )}
          />
        </Group>
      );

    case "PRICING":
      return (
        <Group label="Prix">
          <Field label="Titre">
            <Input
              value={block.props.heading ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, heading: e.target.value || undefined } })}
            />
          </Field>
          <Field label="Description">
            <Textarea
              value={block.props.description ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, description: e.target.value || undefined } })}
            />
          </Field>
          <Field label="Libellé du bouton">
            <Input
              value={block.props.ctaLabel}
              onChange={(e) => onChange({ ...block, props: { ...block.props, ctaLabel: e.target.value } })}
            />
          </Field>
          <p style={hint}>Le prix affiché est celui de l'Offre liée à la page.</p>
        </Group>
      );

    case "CTA":
      return (
        <Group label="Bouton d'appel">
          <Field label="Libellé">
            <Input
              value={block.props.label}
              onChange={(e) => onChange({ ...block, props: { ...block.props, label: e.target.value } })}
            />
          </Field>
          <Field label="Sous-titre (optionnel)">
            <Input
              value={block.props.subtitle ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, subtitle: e.target.value || undefined } })}
            />
          </Field>
          <Field label="Action">
            <select
              value={block.props.action}
              onChange={(e) => onChange({ ...block, props: { ...block.props, action: e.target.value as "buy" | "scroll-to" | "external" } })}
              style={selectStyle}
            >
              <option value="buy">Acheter (Stripe Checkout)</option>
              <option value="scroll-to">Scroll vers ancre</option>
              <option value="external">URL externe</option>
            </select>
          </Field>
          {block.props.action !== "buy" && (
            <Field label="Cible">
              <Input
                value={block.props.target ?? ""}
                onChange={(e) => onChange({ ...block, props: { ...block.props, target: e.target.value || undefined } })}
                placeholder={block.props.action === "scroll-to" ? "#section-id" : "https://…"}
              />
            </Field>
          )}
        </Group>
      );

    case "DIVIDER":
      return (
        <Group label="Séparateur">
          <Field label="Couleur du trait">
            <ColorInput
              value={block.props.color}
              onChange={(v) => onChange({ ...block, props: { ...block.props, color: v } })}
            />
          </Field>
          <Field label="Épaisseur">
            <Input
              value={block.props.thickness ?? ""}
              onChange={(e) => onChange({ ...block, props: { ...block.props, thickness: e.target.value || undefined } })}
              placeholder="ex : 1px, 2px"
            />
          </Field>
          <Field label="Style">
            <select
              value={block.props.style ?? "solid"}
              onChange={(e) =>
                onChange({
                  ...block,
                  props: { ...block.props, style: e.target.value as "solid" | "dashed" | "dotted" },
                })
              }
              style={{
                width: "100%",
                padding: "8px 10px",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
                fontSize: 14,
                fontFamily: "inherit",
              }}
            >
              <option value="solid">Continu</option>
              <option value="dashed">Tirets</option>
              <option value="dotted">Pointillés</option>
            </select>
          </Field>
        </Group>
      );

    case "SPACER":
      return (
        <Group label="Espace">
          <Field label="Hauteur">
            <Input
              value={block.props.height}
              onChange={(e) => onChange({ ...block, props: { ...block.props, height: e.target.value } })}
              placeholder="ex : 64px, 6rem"
            />
          </Field>
        </Group>
      );

    case "COLUMNS": {
      const cols = block.props.columns as Block[][];
      const currentWidths = block.props.widths && block.props.widths.length === cols.length
        ? block.props.widths
        : cols.map(() => 1);
      const currentGap = block.props.gap ?? "24px";
      function writeProps(next: { columns?: Block[][]; widths?: number[]; gap?: string }) {
        onChange({
          id: block.id,
          type: "COLUMNS",
          style: block.style,
          props: {
            columns: next.columns ?? cols,
            widths: next.widths ?? currentWidths,
            gap: next.gap ?? currentGap,
          },
        });
      }
      function setColCount(n: number) {
        let nextCols = cols.slice();
        let nextWidths = currentWidths.slice();
        if (n > cols.length) {
          while (nextCols.length < n) {
            nextCols.push([]);
            nextWidths.push(1);
          }
        } else if (n < cols.length) {
          nextCols = nextCols.slice(0, n);
          nextWidths = nextWidths.slice(0, n);
        }
        writeProps({ columns: nextCols, widths: nextWidths });
      }
      function setColumn(colIdx: number, items: Block[]) {
        const next = cols.slice();
        next[colIdx] = items;
        writeProps({ columns: next });
      }
      function setColumnWidth(colIdx: number, w: number) {
        const next = currentWidths.slice();
        next[colIdx] = Math.max(0.1, w);
        writeProps({ widths: next });
      }
      function resetWidths() {
        writeProps({ widths: cols.map(() => 1) });
      }
      function addNested(colIdx: number, type: BlockType) {
        const b = createBlock(type);
        setColumn(colIdx, [...cols[colIdx], b]);
        onEnterNested?.(colIdx, b.id);
      }
      function moveNested(colIdx: number, idx: number, dir: -1 | 1) {
        const j = idx + dir;
        const col = cols[colIdx];
        if (j < 0 || j >= col.length) return;
        const cp = col.slice();
        [cp[idx], cp[j]] = [cp[j], cp[idx]];
        setColumn(colIdx, cp);
      }
      function removeNested(colIdx: number, idx: number) {
        setColumn(colIdx, cols[colIdx].filter((_, i) => i !== idx));
      }
      return (
        <Group label="Colonnes">
          <Field label="Nombre de colonnes">
            <div style={{ display: "flex", gap: 4 }}>
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  onClick={() => setColCount(n)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    border: `1px solid ${cols.length === n ? COLORS.accent : COLORS.border}`,
                    background: cols.length === n ? COLORS.accent : "transparent",
                    color: cols.length === n ? "#fff" : COLORS.text,
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: "inherit",
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Espacement entre colonnes">
            <Input
              value={currentGap}
              onChange={(e) => writeProps({ gap: e.target.value || "0" })}
              placeholder="ex : 24px"
            />
          </Field>
          {cols.length > 1 && (
            <Field label={`Largeurs (poids) — total ${currentWidths.reduce((a, b) => a + b, 0).toFixed(1)}`}>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {currentWidths.map((w, i) => (
                  <input
                    key={i}
                    type="number"
                    step="0.5"
                    min="0.1"
                    value={w}
                    onChange={(e) => setColumnWidth(i, Number(e.target.value) || 0.1)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 6,
                      fontSize: 14,
                      fontFamily: "inherit",
                      textAlign: "center",
                    }}
                  />
                ))}
                <button
                  type="button"
                  onClick={resetWidths}
                  title="Rééquilibrer"
                  style={{
                    padding: "8px 10px",
                    border: `1px solid ${COLORS.border}`,
                    background: "transparent",
                    color: COLORS.muted,
                    cursor: "pointer",
                    borderRadius: 6,
                    fontSize: 12,
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  ⇆
                </button>
              </div>
              <p style={{ ...hint, marginTop: 4 }}>
                Ex : 1 / 1 / 1 = colonnes égales. 1 / 2 / 1 = colonne du milieu deux fois plus large.
              </p>
            </Field>
          )}
          {cols.map((col, colIdx) => (
            <div key={colIdx} style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                Colonne {colIdx + 1}
              </div>
              {col.length === 0 && (
                <div style={{ fontSize: 12, color: COLORS.muted, fontStyle: "italic", marginBottom: 8 }}>
                  Aucun bloc — ajoutez-en un ci-dessous.
                </div>
              )}
              {col.map((nested, nIdx) => {
                const info = BLOCK_CATALOG[nested.type];
                return (
                  <div
                    key={nested.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 8px",
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 6,
                      marginBottom: 6,
                      background: "#fafaf8",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{info.icon}</span>
                    <button
                      onClick={() => onEnterNested?.(colIdx, nested.id)}
                      style={{
                        flex: 1,
                        textAlign: "left",
                        background: "transparent",
                        border: "none",
                        cursor: onEnterNested ? "pointer" : "default",
                        fontSize: 13,
                        fontFamily: "inherit",
                        color: COLORS.text,
                        padding: 0,
                      }}
                    >
                      {info.label}
                    </button>
                    <NestedIconBtn onClick={() => moveNested(colIdx, nIdx, -1)} disabled={nIdx === 0} title="Monter">↑</NestedIconBtn>
                    <NestedIconBtn onClick={() => moveNested(colIdx, nIdx, 1)} disabled={nIdx === col.length - 1} title="Descendre">↓</NestedIconBtn>
                    <NestedIconBtn onClick={() => removeNested(colIdx, nIdx)} title="Supprimer">✕</NestedIconBtn>
                  </div>
                );
              })}
              <NestedAddPicker onPick={(type) => addNested(colIdx, type)} />
            </div>
          ))}
        </Group>
      );
    }

    case "CUSTOM_HTML":
      return (
        <CustomHtmlEditor
          html={block.props.html}
          onChange={(html) => onChange({ ...block, props: { ...block.props, html } })}
          onPersist={onPersist}
        />
      );
  }
}

// ─────────────────────────────────────────────────────────────
// Éditeur HTML — sidebar compact + plein écran modal
// ─────────────────────────────────────────────────────────────
function CustomHtmlEditor({
  html,
  onChange,
  onPersist,
}: {
  html: string;
  onChange: (h: string) => void;
  onPersist?: () => void;
}) {
  const [fullscreen, setFullscreen] = useState(false);
  const sizeKb = (html.length / 1024).toFixed(1);

  return (
    <>
      <Group label="Code HTML">
        <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 10 }}>
          Bloc HTML personnalisé · {sizeKb} ko · {html.length.toLocaleString("fr-FR")} caractères
        </div>
        <Button onClick={() => setFullscreen(true)} style={{ width: "100%", marginBottom: 12 }}>
          ✎ Éditer en plein écran
        </Button>
        <Field label="Aperçu rapide (lecture seule)">
          <Textarea
            value={html.slice(0, 800) + (html.length > 800 ? "\n\n… (cliquez 'Éditer en plein écran' pour voir/modifier la suite)" : "")}
            readOnly
            style={{ fontFamily: "monospace", fontSize: 11, minHeight: 200, background: "#fafaf8", color: COLORS.muted }}
          />
        </Field>
        <p style={hint}>
          ⚠️ Le HTML est rendu tel quel. Évitez les balises &lt;script&gt; non sûres.
        </p>
      </Group>

      {fullscreen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20,15,30,0.85)",
            zIndex: 2000,
            display: "flex",
            alignItems: "stretch",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              width: "100%",
              maxWidth: 1400,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "14px 20px",
                borderBottom: `1px solid ${COLORS.border}`,
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, flex: 1 }}>Éditeur HTML</div>
              <div style={{ fontSize: 12, color: COLORS.muted }}>
                {sizeKb} ko · {html.length.toLocaleString("fr-FR")} caractères
              </div>
              {onPersist && (
                <Button
                  onClick={() => {
                    onPersist();
                    setFullscreen(false);
                  }}
                >
                  💾 Enregistrer et fermer
                </Button>
              )}
              <Button variant="ghost" onClick={() => setFullscreen(false)}>
                Fermer
              </Button>
            </div>
            <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", overflow: "hidden" }}>
              <div style={{ overflow: "hidden", borderRight: `1px solid ${COLORS.border}` }}>
                <div
                  style={{
                    padding: "8px 16px",
                    background: "#fafaf8",
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontSize: 11,
                    fontWeight: 700,
                    color: COLORS.muted,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                  }}
                >
                  Code HTML
                </div>
                <textarea
                  value={html}
                  onChange={(e) => onChange(e.target.value)}
                  spellCheck={false}
                  style={{
                    width: "100%",
                    height: "calc(100% - 36px)",
                    border: "none",
                    padding: 16,
                    fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
                    fontSize: 13,
                    lineHeight: 1.5,
                    resize: "none",
                    outline: "none",
                    color: "#1a1a1a",
                    background: "#fff",
                    boxSizing: "border-box",
                  }}
                  autoFocus
                />
              </div>
              <div style={{ overflow: "auto", background: "#fff" }}>
                <div
                  style={{
                    padding: "8px 16px",
                    background: "#fafaf8",
                    borderBottom: `1px solid ${COLORS.border}`,
                    fontSize: 11,
                    fontWeight: 700,
                    color: COLORS.muted,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  Aperçu en direct
                </div>
                <iframe
                  title="Aperçu HTML"
                  srcDoc={html}
                  sandbox="allow-scripts allow-same-origin"
                  style={{ width: "100%", height: "calc(100% - 36px)", border: "none" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers UI
// ─────────────────────────────────────────────────────────────
function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span style={{ fontSize: 14 }}>{label}</span>
    </label>
  );
}

function ItemList<T>({
  label,
  items,
  empty,
  onChange,
  renderFields,
}: {
  label: string;
  items: T[];
  empty: () => T;
  onChange: (items: T[]) => void;
  renderFields: (item: T, set: (next: T) => void) => React.ReactNode;
}) {
  function setAt(i: number, next: T) {
    const cp = items.slice();
    cp[i] = next;
    onChange(cp);
  }
  function removeAt(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...items, empty()]);
  }
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
        {label}
      </div>
      {items.map((it, i) => (
        <div key={i} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 12, marginBottom: 10 }}>
          {renderFields(it, (next) => setAt(i, next))}
          <div style={{ textAlign: "right" }}>
            <Button variant="danger" onClick={() => removeAt(i)} style={{ padding: "4px 10px", fontSize: 12 }}>Retirer</Button>
          </div>
        </div>
      ))}
      <Button variant="ghost" onClick={add} style={{ width: "100%" }}>+ Ajouter</Button>
    </div>
  );
}

const hint = {
  fontSize: 12,
  color: COLORS.muted,
  fontStyle: "italic" as const,
  margin: "8px 0",
  lineHeight: 1.5,
};

const selectStyle = {
  width: "100%",
  padding: "8px 10px",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 6,
  fontSize: 14,
  fontFamily: "inherit",
};

function NestedIconBtn({
  onClick,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 24,
        height: 24,
        border: "none",
        background: "transparent",
        color: COLORS.muted,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.35 : 1,
        borderRadius: 4,
        fontSize: 12,
        fontFamily: "inherit",
      }}
    >
      {children}
    </button>
  );
}

function NestedAddPicker({ onPick }: { onPick: (type: BlockType) => void }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          width: "100%",
          padding: "8px",
          border: `1px dashed ${COLORS.border}`,
          background: "transparent",
          color: COLORS.muted,
          cursor: "pointer",
          borderRadius: 6,
          fontSize: 12,
          fontFamily: "inherit",
          marginTop: 4,
        }}
      >
        + Ajouter un bloc
      </button>
    );
  }
  return (
    <div
      style={{
        marginTop: 4,
        padding: 8,
        border: `1px solid ${COLORS.accent}`,
        borderRadius: 6,
        background: "#fafaf8",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
        {NESTABLE_TYPES.map((type) => {
          const info = BLOCK_CATALOG[type];
          return (
            <button
              key={type}
              type="button"
              onClick={() => {
                onPick(type);
                setOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 8px",
                border: `1px solid ${COLORS.border}`,
                background: "#fff",
                cursor: "pointer",
                borderRadius: 4,
                fontSize: 12,
                fontFamily: "inherit",
                color: COLORS.text,
                textAlign: "left",
              }}
            >
              <span>{info.icon}</span>
              <span>{info.label}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => setOpen(false)}
        style={{
          marginTop: 8,
          width: "100%",
          padding: "4px",
          border: "none",
          background: "transparent",
          color: COLORS.muted,
          cursor: "pointer",
          fontSize: 11,
          fontFamily: "inherit",
        }}
      >
        Annuler
      </button>
    </div>
  );
}
